import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ticketsAPI } from "../../services/api";
import { buildSearchParams } from "../../utils/buildSearchParams";

// Async thunk for fetching tickets statistics
export const fetchTicketsStatistics = createAsyncThunk(
  "tickets/fetchTicketsStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getTicketsStatistics();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching open tickets with pagination
export const fetchOpenTickets = createAsyncThunk(
  "tickets/fetchOpenTickets",
  async ({ page = 1, pageSize = 20, communicationFilter = "all" }, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getOpenTickets(
        page,
        pageSize,
        communicationFilter
      );
      return { ...response, pageSize };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching closed tickets with pagination
export const fetchClosedTickets = createAsyncThunk(
  "tickets/fetchClosedTickets",
  async ({ page = 1, pageSize = 20, communicationFilter = "all" }, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getClosedTickets(
        page,
        pageSize,
        communicationFilter
      );
      return { ...response, pageSize };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const searchTickets = createAsyncThunk(
  "tickets/searchTickets",
  async ({ page = 1, pageSize = 20, filters }, { rejectWithValue }) => {
    try {
      const params = buildSearchParams(filters, page, pageSize);
      const response = await ticketsAPI.searchTickets(params);
      return { response, page, pageSize };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserSearchOptions = createAsyncThunk(
  "tickets/fetchUserSearchOptions",
  async (_, { rejectWithValue }) => {
    try {
      return await ticketsAPI.getUserSearchOptions();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Legacy fetchTickets for backward compatibility
export const fetchTickets = createAsyncThunk(
  "tickets/fetchTickets",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getTickets();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for creating a new ticket
export const createTicket = createAsyncThunk(
  "tickets/createTicket",
  async (ticketData, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.createTicket(ticketData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating a ticket
export const updateTicket = createAsyncThunk(
  "tickets/updateTicket",
  async ({ id, ticketData }, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.updateTicket(id, ticketData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for adding a ticket reply
export const addTicketReply = createAsyncThunk(
  "tickets/addTicketReply",
  async ({ ticketId, replyData }, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.addTicketReply(ticketId, replyData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching ticket replies
export const fetchTicketReplies = createAsyncThunk(
  "tickets/fetchTicketReplies",
  async (ticketId, { rejectWithValue }) => {
    try {
      const response = await ticketsAPI.getTicketReplies(ticketId);
      return { ticketId, replies: response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  openTickets: [],
  closedTickets: [],
  ticketReplies: {}, // Store replies by ticket ID
  statistics: {
    total_tickets: 0,
    open_tickets: 0,
    closed_tickets: 0,
    high_priority_tickets: 0,
  },
  pagination: {
    openTickets: {
      currentPage: 1,
      totalPages: 0,
      totalCount: 0,
    },
    closedTickets: {
      currentPage: 1,
      totalPages: 0,
      totalCount: 0,
    },
  },
  searchResults: [],
  searchPagination: {
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    pageSize: 20,
  },
  searchLoading: false,
  searchError: null,
  userSearchOptions: [],
  isLoading: false,
  error: null,
};

const ticketsSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    filterTickets: (state, action) => {
      const { status } = action.payload;
      // This will be handled by the component logic
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch statistics cases
      .addCase(fetchTicketsStatistics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketsStatistics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.statistics = action.payload || {
          total_tickets: 0,
          open_tickets: 0,
          closed_tickets: 0,
          high_priority_tickets: 0,
        };
        state.error = null;
      })
      .addCase(fetchTicketsStatistics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        // Set default statistics on error
        state.statistics = {
          total_tickets: 0,
          open_tickets: 0,
          closed_tickets: 0,
          high_priority_tickets: 0,
        };
      })
      // Fetch open tickets cases
      .addCase(fetchOpenTickets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOpenTickets.fulfilled, (state, action) => {
        state.isLoading = false;
        const { results, count, pageSize = 20 } = action.payload;
        state.openTickets = results || [];
        state.pagination.openTickets.totalCount = count || 0;
        state.pagination.openTickets.totalPages = Math.ceil((count || 0) / pageSize);
        state.error = null;
      })
      .addCase(fetchOpenTickets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch closed tickets cases
      .addCase(fetchClosedTickets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClosedTickets.fulfilled, (state, action) => {
        state.isLoading = false;
        const { results, count, pageSize = 20 } = action.payload;
        state.closedTickets = results || [];
        state.pagination.closedTickets.totalCount = count || 0;
        state.pagination.closedTickets.totalPages = Math.ceil(
          (count || 0) / pageSize
        );
        state.error = null;
      })
      .addCase(fetchClosedTickets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Legacy fetch tickets cases
      .addCase(fetchTickets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;
        const tickets = Array.isArray(payload)
          ? payload
          : payload?.results ?? [];
        state.openTickets = tickets.filter(
          (ticket) => ticket.status === "open"
        );
        state.closedTickets = tickets.filter(
          (ticket) => ticket.status === "closed"
        );
        state.error = null;
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create ticket cases
      .addCase(createTicket.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTicket.fulfilled, (state, action) => {
        state.isLoading = false;
        const newTicket = action.payload;
        if (newTicket.status === "open") {
          state.openTickets.push(newTicket);
        } else {
          state.closedTickets.push(newTicket);
        }
        state.error = null;
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update ticket cases
      .addCase(updateTicket.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTicket.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedTicket = action.payload;

        // Remove from current list
        state.openTickets = state.openTickets.filter(
          (ticket) => ticket.id !== updatedTicket.id
        );
        state.closedTickets = state.closedTickets.filter(
          (ticket) => ticket.id !== updatedTicket.id
        );

        // Add to appropriate list
        if (updatedTicket.status === "open") {
          state.openTickets.push(updatedTicket);
        } else {
          state.closedTickets.push(updatedTicket);
        }

        state.error = null;
      })
      .addCase(updateTicket.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add ticket reply cases
      .addCase(addTicketReply.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addTicketReply.fulfilled, (state, action) => {
        state.isLoading = false;
        const newReply = action.payload;
        const ticketId = newReply.ticket;

        // Add reply to the appropriate ticket's replies
        if (!state.ticketReplies[ticketId]) {
          state.ticketReplies[ticketId] = [];
        }
        state.ticketReplies[ticketId].push(newReply);

        // Update reply count in tickets
        const allTickets = [...state.openTickets, ...state.closedTickets];
        const ticketIndex = allTickets.findIndex(
          (t) => (t.ticket_id || t.id) === ticketId
        );
        if (ticketIndex !== -1) {
          const ticket = allTickets[ticketIndex];
          ticket.reply_count = (ticket.reply_count || 0) + 1;

          // Update in the appropriate array
          if (ticket.status === "open") {
            const openIndex = state.openTickets.findIndex(
              (t) => (t.ticket_id || t.id) === ticketId
            );
            if (openIndex !== -1) {
              state.openTickets[openIndex] = ticket;
            }
          } else {
            const closedIndex = state.closedTickets.findIndex(
              (t) => (t.ticket_id || t.id) === ticketId
            );
            if (closedIndex !== -1) {
              state.closedTickets[closedIndex] = ticket;
            }
          }
        }

        state.error = null;
      })
      .addCase(addTicketReply.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch ticket replies cases
      .addCase(fetchTicketReplies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketReplies.fulfilled, (state, action) => {
        state.isLoading = false;
        const { ticketId, replies } = action.payload;
        state.ticketReplies[ticketId] = replies;
        state.error = null;
      })
      .addCase(fetchTicketReplies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(searchTickets.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchTickets.fulfilled, (state, action) => {
        state.searchLoading = false;
        const { response, page, pageSize } = action.payload;
        const results = response?.results ?? [];
        const count = response?.count ?? 0;
        state.searchResults = results;
        state.searchPagination = {
          currentPage: page,
          totalPages: Math.ceil(count / pageSize) || 0,
          totalCount: count,
          pageSize,
        };
        state.searchError = null;
      })
      .addCase(searchTickets.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload;
      })
      .addCase(fetchUserSearchOptions.fulfilled, (state, action) => {
        state.userSearchOptions = action.payload || [];
      });
  },
});

export const { clearError, filterTickets } = ticketsSlice.actions;

// Selectors
export const selectTicketReplies = (state, ticketId) => {
  return state.tickets.ticketReplies[ticketId] || [];
};

export default ticketsSlice.reducer;
